import { SCHEMA } from '@vedalib/editor/lib/brand'
import React, { useCallback, useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { Typography } from 'components/uiKit/Typography'
import { BrandButtonFont, BrandFormFontBase, BrandGoBack } from 'services/Branding/components'
import { FONT_FIELDS } from 'services/Branding/fieldConfigs'
import 'services/Branding/fieldConfigs/useFieldConfigs'
import { genSchema } from 'services/Branding/genSchema'
import { useElementFontCss, useElementFontValues, useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type {
  CourseStructureBrandForms,
  CourseStructureFormBrandType,
} from './courseStructureTypes'

const FONT_FORM_SCHEMA = genSchema(SCHEMA.courseStructure.font, {
  chapter: FONT_FIELDS.base,
  section: FONT_FIELDS.base,
  description: FONT_FIELDS.base,
})

const TypographyChapter: CourseStructureFormBrandType = ({ setForm, device, subConfigs }) => {
  const fontPath = useMemo(() => ['font', device], [device])
  const fontSchema = useUpdateSchema(fontPath, subConfigs, FONT_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <BrandFormFontBase font={fontSchema.chapter}>
      <BrandGoBack onClick={handleSetTypography}>
        <Typography weight='medium'>{t('elements.courseStructure.tags.chapter')}</Typography>
      </BrandGoBack>
      <Typography styleType='hint'>{t('page.brand.fonts.inheritHint')}</Typography>
    </BrandFormFontBase>
  )
}

const TypographySection: CourseStructureFormBrandType = ({ setForm, device, subConfigs }) => {
  const fontPath = useMemo(() => ['font', device], [device])
  const fontSchema = useUpdateSchema(fontPath, subConfigs, FONT_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <BrandFormFontBase font={fontSchema.section}>
      <BrandGoBack onClick={handleSetTypography}>
        <Typography weight='medium'>{t('elements.courseStructure.tags.section')}</Typography>
      </BrandGoBack>
      <Typography styleType='hint'>{t('page.brand.fonts.inheritHint')}</Typography>
    </BrandFormFontBase>
  )
}

const TypographyDescription: CourseStructureFormBrandType = ({ setForm, device, subConfigs }) => {
  const fontPath = useMemo(() => ['font', device], [device])
  const fontSchema = useUpdateSchema(fontPath, subConfigs, FONT_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <BrandFormFontBase font={fontSchema.description}>
      <BrandGoBack onClick={handleSetTypography}>
        <Typography weight='medium'>{t('elements.courseStructure.tags.description')}</Typography>
      </BrandGoBack>
      <Typography styleType='hint'>{t('page.brand.fonts.inheritHint')}</Typography>
    </BrandFormFontBase>
  )
}

const CourseStructureTypography: CourseStructureFormBrandType = ({
  device,
  edited,
  element,
  setForm,
}) => {
  const values = useElementFontValues('courseStructure', device, element?.font)
  const styles = useElementFontCss('courseStructure', device, element?.font)

  const goBack = useCallback(() => setForm?.(BrandFormEnum.STYLE), [setForm])

  return (
    <Padding bottom gap sides>
      <BrandGoBack onClick={goBack}>
        <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
      </BrandGoBack>
      <BrandButtonFont
        edited={edited.font?.chapter}
        font='chapter'
        onClick={setForm}
        styles={styles.chapter}
        values={values.chapter}
      >
        {t('elements.courseStructure.tags.chapter')}
      </BrandButtonFont>
      <BrandButtonFont
        edited={edited.font?.section}
        font='section'
        onClick={setForm}
        styles={styles.section}
        values={values.section}
      >
        {t('elements.courseStructure.tags.section')}
      </BrandButtonFont>
      <BrandButtonFont
        edited={edited.font?.description}
        font='description'
        onClick={setForm}
        styles={styles.description}
        values={values.description}
      >
        {t('elements.courseStructure.tags.description')}
      </BrandButtonFont>
    </Padding>
  )
}

const FORM_MAP: CourseStructureBrandForms = {
  [BrandFormEnum.TYPOGRAPHY]: CourseStructureTypography,
  chapter: TypographyChapter,
  section: TypographySection,
  description: TypographyDescription,
}

export default FORM_MAP
